<template>
    <!-- Teleported to <body>: iOS Safari un-pins position:fixed elements that sit
         inside wrappers with overflow/compositing side effects. The ob-corp class
         is mirrored here because the strip no longer lives under #ob-shell. -->
    <Teleport to="body">
    <div class="strip" :class="{ 'ob-corp': store.isCorp }">
        <div class="strip-inner">
            <div class="strip-left">
                <span class="strip-live"><span class="strip-dot" />Network live</span>
                <span class="strip-msg">{{ msg }}</span>
            </div>
            <div class="strip-right">
                <div class="strip-readout">
                    <span class="strip-val">{{ readout }}</span>
                    <span class="strip-unit">Mbps</span>
                </div>
                <button class="strip-btn ob-press" @click="store.runSpeedTest">{{ btnLabel }}</button>
            </div>
        </div>
        <div class="strip-bar-track">
            <div class="ob-speedbar" :style="{ width: barW }" />
        </div>
    </div>
    </Teleport>
</template>

<script lang="ts" setup>
    import { computed } from 'vue'

    import { useLandingStore } from '@/stores/landing'

    const store = useLandingStore()

    const readout = computed(() => (store.speedState === 'idle' || store.speedState === 'error') ? '—' : String(store.speedVal || 0))
    const btnLabel = computed(() => {
        switch (store.speedState) {
            case 'running': return 'Testing…'
            case 'done': return 'Test again'
            case 'error': return 'Try again'
            default: return 'Test my speed'
        }
    })
    const msg = computed(() => {
        switch (store.speedState) {
            case 'running': return 'Measuring your connection…'
            case 'done': return 'Live result on your connection.'
            case 'error': return 'The test could not run right now — please try again.'
            default: return 'Curious what you are getting right now? Check your speed.'
        }
    })
    const barW = computed(() => (store.speedState === 'idle' || store.speedState === 'error') ? '0%' : Math.min(100, Math.round(((store.speedVal || 0) / 100) * 100)) + '%')
</script>

<style scoped>
    .strip {
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 55;
        background: var(--ob-strip-bg);
        backdrop-filter: blur(16px);
        -webkit-backdrop-filter: blur(16px);
        border-top: 1px solid var(--ob-navy-a12);
        padding-bottom: env(safe-area-inset-bottom);
        /* own compositing layer — keeps the fixed strip pinned during iOS momentum scroll */
        transform: translateZ(0);
    }
    /* backdrop-filter on a fixed element makes iOS Safari detach it mid-scroll — solid bg on mobile */
    @media (max-width: 900px) {
        .strip { backdrop-filter: none; -webkit-backdrop-filter: none; background: var(--ob-strip-bg-solid); }
    }
    .strip-inner {
        max-width: 1240px;
        margin: 0 auto;
        padding: 11px 44px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 20px;
    }
    .strip-left { display: flex; align-items: center; gap: 16px; min-width: 0; }
    .strip-live {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-size: 11.5px;
        font-weight: 700;
        letter-spacing: .11em;
        text-transform: uppercase;
        color: var(--ob-blue-deep);
        white-space: nowrap;
    }
    .strip-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--ob-success);
        box-shadow: 0 0 0 4px var(--ob-success-ring);
    }
    .strip-msg { font-size: 14px; color: var(--ob-ink-sub); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .strip-right { display: flex; align-items: center; gap: 18px; }
    .strip-readout { display: flex; align-items: baseline; gap: 6px; }
    .strip-val { font-size: 23px; font-weight: 800; font-variant-numeric: tabular-nums; line-height: 1; color: var(--ob-ink); }
    .strip-unit { font-size: 12px; color: var(--ob-ink-sub); }
    .strip-btn {
        border: none;
        cursor: pointer;
        background: var(--ob-blue);
        color: var(--ob-white);
        font-family: 'Poppins', sans-serif;
        font-weight: 700;
        font-size: 14px;
        padding: 11px 22px;
        border-radius: 999px;
        box-shadow: var(--ob-shadow-cta);
        white-space: nowrap;
    }
    .strip-bar-track { height: 3px; background: var(--ob-navy-a08); }
    .ob-speedbar { height: 100%; background: var(--ob-grad-speedbar); transition: width .16s linear; }

    @media (max-width: 700px) {
        .strip-inner { padding: 9px 20px; gap: 12px; }
        .strip-msg { display: none; }
        .strip-right { gap: 12px; }
        .strip-btn { font-size: 13px; padding: 9px 16px; }
    }
</style>
